/**
 * Whether this deployment has the keybook at all (0179, platform setting).
 *
 * A tiny external store rather than context, and in @papan/core-ui rather
 * than the web app, because the readers span packages that cannot reach each
 * other: the shells (apps/web) hide the taskbar item, deck, mobile tab and
 * Settings → Secure Storage; the chat composer (features/agent/ui) hides its
 * session-keybook affordance. The ONE writer is apps/web's landing fetch
 * (`loadLandingExperience`), which reads `/api/public/experience` before
 * first paint and calls `setKeybookAvailable`.
 *
 * Seeds `true` — the shipped state of every deployment — so a consumer
 * rendered before the fetch lands (or in a test with no fetch at all) behaves
 * exactly as the product always has. DISCLOSURE only, like every other
 * reader: the server's /api/keybook gate is the enforcement, so a stale
 * `true` shows affordances whose requests are refused in words, never a
 * widened grant.
 */
import { useSyncExternalStore } from 'react'

let available = true
const listeners = new Set<() => void>()

export function setKeybookAvailable(v: boolean): void {
  if (v === available) return
  available = v
  for (const l of listeners) l()
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)
  return () => { listeners.delete(onStoreChange) }
}

function snapshot(): boolean {
  return available
}

export function useKeybookAvailable(): boolean {
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

/** Synchronous read for event handlers and non-React code. */
export function keybookAvailable(): boolean {
  return available
}
